// Vite 7 is stricter in terms of how it handles tree-shaking
// - gcash only needs GCashTheme, so this makes sense
import { GCashTheme } from '@talos/kyoko/src/styles/themes/GCashTheme';

import { GlobalAlert } from '@src/components/Alert';
import { FooterDisclaimer } from '@src/components/FooterDisclaimer';
import { AuthCodeModalProvider } from '@src/contexts/AuthCodeModalContext';
import { PortfolioLayout } from '@src/pages/Portfolio';
import { SubRoute } from '@src/pages/SubRoute';
import { WatchlistLayout } from '@src/pages/Watchlist';
import { type Security, useOMSDependencies } from '@talos/kyoko';
import { useCallback, useRef } from 'react';
import { Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import SpotlightProvider from './hooks/SpotlightOverlayContext';
import { StackableContext } from './hooks/useStackableRouteId';
import { TransactionsLayout } from './pages/Transactions';
import { NavigationProvider } from './Providers/NavigationProvider';
import { useHomeCurrencyConversions } from './Providers/useHomeCurrencyConversions';

const getStrategiesGivenSecurity = (s: Security) => s.SupportedStrategies ?? [];
export function AppRoutes() {
  useHomeCurrencyConversions();
  useOMSDependencies({ getStrategiesGivenSecurity });
  const scrollRef = useRef<HTMLDivElement>(null);
  const onHandleClickTab = useCallback((path: string) => {
    if (document.location.pathname === path) {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <GlobalAlert />
      <ThemeProvider theme={GCashTheme}>
        <AuthCodeModalProvider>
          <SpotlightProvider>
            <NavigationProvider>
              <div
                className="relative mx-auto flex size-full grow flex-col items-center overflow-y-auto"
                ref={scrollRef}
              >
                <Routes>
                  <Route index element={<Navigate to="/watchlist" />} />
                  <Route
                    path="/watchlist/*"
                    element={
                      <StackableContext.Provider value="watchlist">
                        <SubRoute layout={<WatchlistLayout />} />
                        <FooterDisclaimer />
                      </StackableContext.Provider>
                    }
                  />
                  <Route
                    path="/transactions/*"
                    element={
                      <StackableContext.Provider value="transactions">
                        <SubRoute layout={<TransactionsLayout />} />
                      </StackableContext.Provider>
                    }
                  />

                  <Route
                    path="/portfolio/*"
                    element={
                      <StackableContext.Provider value="portfolio">
                        <SubRoute layout={<PortfolioLayout />} />
                        <FooterDisclaimer />
                      </StackableContext.Provider>
                    }
                  />
                </Routes>

                <div className="w-full p-8" /* This is a margin for the tabs */ />
                {/* TODO(robertz): separate the scroll or store them on a per tab basis? */}
                <div
                  id="tab-list"
                  className="shadow-app-low bg-color-bg-main fixed bottom-0 flex w-full shrink justify-around gap-4 p-2 text-xs font-semibold tracking-wide"
                >
                  <NavLink
                    className={({ isActive }) => getClassName(isActive)}
                    to="/watchlist"
                    data-testid="tab-watchlist"
                    onClick={() => onHandleClickTab('/watchlist')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.5946 4.27713C12.2764 3.99886 11.8109 3.98175 11.4744 4.22537L11.409 4.27713L3.40902 11.2771C3.03499 11.6044 2.99688 12.1736 3.32406 12.5476C3.65133 12.9217 4.22049 12.9598 4.59457 12.6326L5.10178 12.1888V20.9545C5.10178 21.4516 5.50512 21.8549 6.00217 21.8549H10.0022C10.4992 21.8549 10.9026 21.4516 10.9026 20.9545V16.9545C10.9026 16.347 11.3947 15.8549 12.0022 15.8549C12.6097 15.8549 13.1018 16.347 13.1018 16.9545V20.9545C13.1018 21.4516 13.5051 21.8549 14.0022 21.8549H18.0022C18.4992 21.8549 18.9026 21.4516 18.9026 20.9545V12.1894L19.409 12.6326L19.4813 12.6892C19.8532 12.9535 20.3726 12.8984 20.6795 12.5476C20.9862 12.197 20.972 11.6751 20.661 11.3416L20.5946 11.2771L12.5946 4.27713ZM17.1018 10.6137L12.0018 6.15115L6.90256 10.6131V20.0551H9.10178V16.9545C9.10178 15.3529 10.4005 14.0542 12.0022 14.0542C13.6038 14.0542 14.9026 15.3529 14.9026 16.9545V20.0551H17.1018V10.6137Z"
                        fill="currentColor"
                      />
                    </svg>

                    <span>Home</span>
                  </NavLink>
                  <NavLink
                    className={({ isActive }) => getClassName(isActive)}
                    to="/transactions"
                    data-testid="tab-transactions"
                    onClick={() => onHandleClickTab('/transactions')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                      <path
                        opacity="0.4"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14.0898 8.05908L13.998 8.0542L7.99805 8.0542C7.50099 8.0542 7.09766 8.45753 7.09766 8.95459C7.09766 9.45165 7.50099 9.85498 7.99805 9.85498L13.998 9.85498L14.0898 9.8501C14.5438 9.80412 14.8984 9.42066 14.8984 8.95459C14.8984 8.48852 14.5438 8.10506 14.0898 8.05908ZM9.99805 12.0542L10.0898 12.0591C10.5438 12.1051 10.8984 12.4885 10.8984 12.9546C10.8984 13.4207 10.5438 13.8041 10.0898 13.8501L9.99805 13.855H7.99805C7.50099 13.855 7.09766 13.4516 7.09766 12.9546C7.09766 12.4575 7.50099 12.0542 7.99805 12.0542H9.99805ZM12.9984 18.5736C13.3402 18.5736 13.6176 18.2962 13.6176 17.9545C13.6176 16.6394 14.6833 15.5736 15.9984 15.5736C16.3402 15.5736 16.6176 15.2962 16.6176 14.9545C16.6176 14.6128 16.3402 14.3354 15.9984 14.3354C13.9999 14.3354 12.3793 15.9559 12.3793 17.9545C12.3793 18.2962 12.6567 18.5736 12.9984 18.5736ZM15.9984 16.3549C15.6671 16.3549 15.3988 16.6231 15.3988 16.9545V18.0971C15.3988 18.249 15.4563 18.3961 15.56 18.5072L16.3598 19.3637L16.4516 19.4438C16.679 19.6053 16.9955 19.5906 17.2074 19.393C17.4193 19.1952 17.4564 18.88 17.3109 18.642L17.2367 18.5453L16.598 17.8598V16.9545L16.5863 16.8334C16.5303 16.5601 16.2883 16.3549 15.9984 16.3549ZM16.0336 20.6449C16.2044 20.6427 16.3449 20.7797 16.3471 20.9506C16.3491 21.1213 16.2121 21.2619 16.0414 21.2641H15.9555C15.7847 21.2619 15.6478 21.1213 15.6498 20.9506C15.652 20.7797 15.7924 20.6427 15.9633 20.6449H16.0336ZM13.6342 19.8266C13.7411 19.7225 13.9039 19.7109 14.0229 19.7914L14.0717 19.8324L14.1205 19.8822C14.2428 20.0014 14.2454 20.1964 14.1264 20.3188C14.0071 20.4411 13.8112 20.4439 13.6889 20.3246L13.6283 20.2641C13.5091 20.1417 13.5118 19.9458 13.6342 19.8266ZM17.9262 19.8324C18.0454 19.7101 18.2403 19.7075 18.3627 19.8266C18.4851 19.9458 18.4878 20.1417 18.3686 20.2641L18.308 20.3246C18.1856 20.4439 17.9897 20.4411 17.8705 20.3188C17.7514 20.1964 17.754 20.0004 17.8764 19.8813L17.8907 19.8674C17.9026 19.8558 17.9146 19.8443 17.9262 19.8324ZM18.6889 17.9193C18.6867 17.7485 18.8237 17.6081 18.9945 17.6059C19.1439 17.6041 19.2696 17.7085 19.3002 17.849L19.308 17.9115V17.9975C19.3058 18.1682 19.1652 18.3051 18.9945 18.3031C18.8237 18.3009 18.6867 18.1605 18.6889 17.9896V17.9193ZM17.8705 15.5902C17.975 15.483 18.138 15.4673 18.2592 15.5453L18.308 15.5844L18.3686 15.6449C18.4878 15.7673 18.4851 15.9632 18.3627 16.0824C18.2557 16.1865 18.093 16.1981 17.974 16.1176L17.9252 16.0766L17.9113 16.0622C17.8998 16.0503 17.8882 16.0383 17.8764 16.0268C17.7541 15.9076 17.7514 15.7126 17.8705 15.5902ZM10.0898 16.0591L9.99805 16.0542H7.99805C7.50099 16.0542 7.09766 16.4575 7.09766 16.9546C7.09766 17.4516 7.50099 17.855 7.99805 17.855H9.99805L10.0898 17.8501C10.5438 17.8041 10.8984 17.4207 10.8984 16.9546C10.8984 16.4885 10.5438 16.1051 10.0898 16.0591Z"
                        fill="currentColor"
                      />
                      <path
                        d="M17 11.9545V6.83693C17 5.79734 16.1572 4.95459 15.1177 4.95459H6.88234C5.84275 4.95459 5 5.79734 5 6.83693V19.0722C5 20.1118 5.84275 20.9546 6.88234 20.9546H10"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Transactions</span>
                  </NavLink>
                  <NavLink
                    className={({ isActive }) => getClassName(isActive)}
                    to="/portfolio"
                    data-testid="tab-portfolio"
                    onClick={() => onHandleClickTab('/portfolio')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                      <path
                        d="M8 7.95459H4C3.44772 7.95459 3 8.40231 3 8.95459V12.9546M8 7.95459V5.95459C8 5.40231 8.44772 4.95459 9 4.95459H15C15.5523 4.95459 16 5.40231 16 5.95459V7.95459M8 7.95459H16M16 7.95459H20C20.5523 7.95459 21 8.40231 21 8.95459V12.9546M3 12.9546V18.9546C3 19.5069 3.44772 19.9546 4 19.9546H20C20.5523 19.9546 21 19.5069 21 18.9546V12.9546M3 12.9546L11.8378 14.4071C11.9452 14.4248 12.0548 14.4248 12.1622 14.4071L21 12.9546"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                    <span>Portfolio</span>
                  </NavLink>
                </div>
              </div>
            </NavigationProvider>
          </SpotlightProvider>
        </AuthCodeModalProvider>
      </ThemeProvider>
    </>
  );
}
// (isActive ? 'text-color-bg-primary' : '') + ' ' + 'flex flex-col items-center'}
const getClassName = (isActive: boolean) => {
  return `${isActive ? `text-color-text-primary` : 'text-color-text-weaker'} flex flex-col items-center flex-1`;
};
